"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import type { FxRatesExplorerComponent } from "../../types";
import { Activity, ArrowDownRight, ArrowLeftRight, ArrowUpRight, Calendar, Check, ChevronDown, Download, Search, TrendingDown, TrendingUp } from "lucide-react";
import { useLocale, useT } from "../../runtime/RendererRuntime";
import { t, type UiStringKey } from "../../i18n/strings";

type Rate = { code: string; name: string; buy: number; sell: number; change1d: number; change7d: number; unit?: number };
type HistoryPoint = { date: string; buy: number; average: number; sell: number; unit?: number };
type ChartMode = "level" | "index";

// ----- Plages du graphique -----
// Le pas de l'historique n'est pas constant : jours ouvres, jours feries, et
// au-dela d'un an une cotation par semaine, pour que cinq ans d'historique
// tiennent dans un bloc raisonnable. Decouper au NOMBRE de points faisait donc
// deriver la fenetre, « 1 an » pouvant couvrir bien plus d'un an. On decoupe
// sur la DATE, seule mesure fidele a ce que le bouton annonce.
export type FxChartRange = "1m" | "3m" | "6m" | "1y" | "5y";
/** Ordre d'affichage de reference, et liste des valeurs acceptees. */
export const FX_CHART_RANGES: readonly FxChartRange[] = ["1m", "3m", "6m", "1y", "5y"] as const;
/**
 * Plages proposees quand le contenu n'en impose pas. « 5y » en est absente a
 * dessein : elle ne vaut que sur un module dont l'historique porte cinq ans.
 */
export const FX_CHART_RANGES_DEFAUT: readonly FxChartRange[] = ["1m", "3m", "6m", "1y"] as const;
const RANGE_MONTHS: Record<FxChartRange, number> = { "1m": 1, "3m": 3, "6m": 6, "1y": 12, "5y": 60 };
/** Repli au compte, utilise uniquement quand la serie n'a pas de dates. */
const RANGE_APPROX_POINTS: Record<FxChartRange, number> = { "1m": 22, "3m": 66, "6m": 132, "1y": 250, "5y": 1250 };

/**
 * Retient les plages valides du contenu, dans l'ordre de reference.
 *
 * Accepte le tableau des fragments comme la chaine « 1m,3m,6m,1y,5y » que
 * produit la liste deroulante de l'admin : un selecteur ne rend pas de tableau,
 * et refuser la chaine ferait retomber le module sur le defaut sans rien dire.
 * Rien de reconnaissable : le defaut.
 */
function resoudrePlages(brut: unknown): readonly FxChartRange[] {
  const brutes = Array.isArray(brut) ? brut : typeof brut === "string" ? brut.split(",") : null;
  if (!brutes) return FX_CHART_RANGES_DEFAUT;
  const demandees = new Set(brutes.map((r) => String(r).trim().toLowerCase()));
  const retenues = FX_CHART_RANGES.filter((r) => demandees.has(r));
  return retenues.length ? retenues : FX_CHART_RANGES_DEFAUT;
}

/** Garde les points dont la date tombe dans la fenetre, bornee par le dernier point. */
function windowByDate(points: HistoryPoint[], range: FxChartRange): HistoryPoint[] {
  const last = points[points.length - 1]?.date;
  if (!last) return points;
  const borne = new Date(`${last}T00:00:00Z`);
  if (Number.isNaN(borne.getTime())) return points.slice(-RANGE_APPROX_POINTS[range]);
  borne.setUTCMonth(borne.getUTCMonth() - RANGE_MONTHS[range]);
  const depuis = borne.toISOString().slice(0, 10);
  return points.filter((p) => p.date >= depuis);
}

// ----- Site palette -----
// Brand blue + body navy. Same tokens PageHeader, MarketsBoard and
// DocumentCard use, so the converter reads as part of the same site
// rather than a detached widget. Avoids the legacy
// #0069B4 / #0a4a8a / #0b3f79 trio that drifted from the brand when this
// page was first scaffolded. No accent orange : the chrome stays in the
// blue family across the renderer.
const COLOR_PRIMARY = "#005CA9";
const COLOR_BODY = "#0F2849";

function toMid(r: Rate): number { return (r.buy + r.sell) / 2; }
function toUnitMid(r: Rate): number { return toMid(r) / Math.max(1, r.unit ?? 1); }
function percentChange(values: number[]): number {
  const first = values[0];
  const last = values[values.length - 1];
  return values.length > 1 && first !== 0 ? (last - first) / first : 0;
}
function annualizedVolatility(values: number[]): number {
  const returns = values.slice(1).map((value, index) => values[index] === 0 ? 0 : value / values[index] - 1);
  if (returns.length < 2) return 0;
  const mean = returns.reduce((sum, value) => sum + value, 0) / returns.length;
  const variance = returns.reduce((sum, value) => sum + (value - mean) ** 2, 0) / (returns.length - 1);
  return Math.sqrt(variance) * Math.sqrt(252);
}
/**
 * Locale-aware `toLocaleString`: the site locale maps to a full formatting
 * tag ("fr"→"fr-FR", "en"→"en-GB", anything else passes through as-is). An
 * unknown/invalid tag falls back to fr-FR instead of crashing the render.
 */
function formatNumber(n: number, locale: string, options?: Intl.NumberFormatOptions): string {
  const tag = locale === "fr" ? "fr-FR" : locale === "en" ? "en-GB" : locale;
  try {
    return n.toLocaleString(tag, options);
  } catch {
    return n.toLocaleString("fr-FR", options);
  }
}
/**
 * 4-decimal formatting (e.g. "2 247,0311" in French) : identical to the home
 * MarketsBoard, so the same value reads the same way across pages.
 */
function formatAmount(n: number, locale: string): string {
  return formatNumber(n, locale, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}
/** 2-4 decimal formatting for converter results : keeps small amounts legible. */
function formatConversion(n: number, locale: string): string {
  return formatNumber(n, locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

// ---------------------------------------------------------------------------
// MiniSparkline : a tiny inline trend line used inside the rate table rows
// and the secondary-currency chips on the hero. Pure SVG, no axes, no
// tooltip, just the shape. Colour is brand blue on positive delta and
// rose on negative, so the user reads "trend" at a glance.
// ---------------------------------------------------------------------------
function MiniSparkline({ data, color, width = 80, height = 28 }: { data: number[]; color: string; width?: number; height?: number }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data); const max = Math.max(...data);
  const range = Math.max(1e-6, max - min);
  const P = 2;
  const pts = data.map((v, i) => {
    const x = P + (i / (data.length - 1)) * (width - P * 2);
    const y = P + (1 - (v - min) / range) * (height - P * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const d = `M ${pts[0]} L ${pts.slice(1).join(" L ")}`;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true" className="overflow-visible">
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// MainChart : the focused chart for the actively-selected currency in the
// right column. Bigger than the row sparkline; gradient fill underneath,
// hover crosshair with a tooltip pill, dotted gridlines.
// ---------------------------------------------------------------------------
function MainChart({
  data,
  comparison = [],
  dates = [],
  mode = "level",
  color = COLOR_PRIMARY,
  height = 250,
}: {
  data: number[];
  comparison?: number[];
  dates?: string[];
  mode?: ChartMode;
  color?: string;
  height?: number;
}) {
  const locale = useLocale();
  const tr = useT();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  // Largeur MESURÉE du conteneur, pour que le viewBox soit en pixels réels.
  // Avec une largeur fixe de 700 et `h-auto`, la hauteur rendue suivait la
  // largeur du panneau : 250 px de dessin devenaient 389 px sur un bureau à
  // 1440. En calant W sur la mesure, la hauteur vaut exactement `height`.
  const [boxWidth, setBoxWidth] = useState(700);
  useEffect(() => {
    const element = wrapRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const measured = Math.round(entries[0]?.contentRect.width ?? 0);
      if (measured > 0) setBoxWidth(measured);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  if (!data || data.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
        {tr("noHistoryYet")}
      </p>
    );
  }
  const normalize = (values: number[]): number[] => {
    if (mode === "level" || values.length === 0 || values[0] === 0) return values;
    return values.map(value => value / values[0] * 100);
  };
  const primaryValues = normalize(data);
  const comparisonValues = normalize(comparison);
  const W = boxWidth; const H = height;
  const PL = 62; const PR = 16; const PT = 28; const PB = 34;
  const combined = [...primaryValues, ...comparisonValues];
  const min = Math.min(...combined); const max = Math.max(...combined);
  const range = Math.max(1e-6, max - min);
  const buildPoints = (values: number[]) => values.map((v, i) => {
    const x = (i / Math.max(1, values.length - 1)) * (W - PL - PR) + PL;
    const y = H - PB - ((v - min) / range) * (H - PT - PB);
    return { x, y, v };
  });
  const points = buildPoints(primaryValues);
  const comparisonPoints = buildPoints(comparisonValues);
  const pathFor = (items: Array<{ x: number; y: number }>): string => items.length === 0
    ? ""
    : `M ${items[0].x},${items[0].y} L ${items.slice(1).map(point => `${point.x},${point.y}`).join(" L ")}`;
  const d = pathFor(points);
  const comparisonPath = pathFor(comparisonPoints);
  const area = `${d} L ${points[points.length - 1].x},${H - PB} L ${PL},${H - PB} Z`;
  const gridLines = Array.from({ length: 4 }, (_, i) => H - PB - ((i + 1) / 5) * (H - PT - PB));
  const yLabels = [min, min + range * 0.5, max];
  const currentIdx = hoverIdx ?? points.length - 1;
  const current = points[currentIdx];
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect(); if (!rect) return;
    const x = e.clientX - rect.left;
    const t = Math.max(0, Math.min(1, (x - PL) / (rect.width - PL - PR)));
    const idx = Math.round(t * (points.length - 1));
    setHoverIdx(idx);
  };
  const onLeave = () => setHoverIdx(null);
  return (
    <div ref={wrapRef} className="w-full">
    <svg ref={svgRef} onMouseMove={onMove} onMouseLeave={onLeave} viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet" role="img" aria-label={tr("rateTrend")}>
      <defs>
        <linearGradient id="fx-explorer-gradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {gridLines.map((y, idx) => (<line key={idx} x1={PL} y1={y} x2={W - PR} y2={y} stroke="#e2e8f0" strokeDasharray="4 4" />))}
      <line x1={PL} y1={H - PB} x2={W - PR} y2={H - PB} stroke="#cbd5e1" />
      {yLabels.map((val, idx) => {
        const y = H - PB - ((val - min) / range) * (H - PT - PB);
        return <text key={idx} x={4} y={y + 5} fontSize={11} fill="#64748b">{formatNumber(val, locale, { maximumFractionDigits: mode === "index" ? 1 : 2 })}</text>;
      })}
      <path d={area} fill="url(#fx-explorer-gradient)" />
      <path d={d} stroke={color} strokeWidth="2.5" fill="none" />
      {comparisonPath ? <path d={comparisonPath} stroke="#f59e0b" strokeWidth="2" strokeDasharray="7 5" fill="none" /> : null}
      {dates.length > 0 ? <>
        <text x={PL} y={H - 8} fontSize={11} fill="#64748b">{dates[0]}</text>
        <text x={W - PR} y={H - 8} fontSize={11} textAnchor="end" fill="#64748b">{dates[dates.length - 1]}</text>
      </> : null}
      <g pointerEvents="none">
        <line x1={current.x} y1={PT} x2={current.x} y2={H - PB} stroke="#94a3b8" strokeDasharray="4 4" />
        <circle cx={current.x} cy={current.y} r="4.5" fill="#fff" stroke={color} strokeWidth="2.5" />
        <rect x={Math.min(Math.max(current.x - 58, PL), W - PR - 116)} y={PT - 7} width="116" height="25" rx="4" fill={COLOR_BODY} />
        <text x={Math.min(Math.max(current.x, PL + 58), W - PR - 58)} y={PT + 10} fontSize={11} fill="#fff" textAnchor="middle">{dates[currentIdx] ? `${dates[currentIdx]} · ` : ""}{formatNumber(current.v, locale, { maximumFractionDigits: mode === "index" ? 2 : 4 })}</text>
      </g>
    </svg>
    </div>
  );
}

/** Exchange rate explorer renderer with hero, converter, table and chart. */
export function FxRatesExplorerRenderer({ component }: { component: FxRatesExplorerComponent }) {
  const locale = useLocale();
  const tr = useT();
  const content = component.content ?? {};
  const RATES: Rate[] = (content as any).rates ?? [];
  const SERIES: Record<string, number[]> = (content as any).series ?? {};
  const HISTORY: Record<string, HistoryPoint[]> = (content as any).history ?? {};
  const variant: "converter" | "market" = (content as any).variant === "market" ? "market" : "converter";
  // Interrupteurs de section pilotés par le CMS. Absents => tout s'affiche,
  // donc un contenu déjà publié rend exactement comme avant. C'est ce qui
  // permet à la page des cours et au convertisseur d'avoir un métier distinct
  // au lieu de se répéter.
  const showAnalytics = (content as any).showAnalytics !== false;
  const showChart = (content as any).showChart !== false;
  const plages = resoudrePlages((content as any).chartRanges);
  const showAllCurrencies = (content as any).showAllCurrencies !== false;
  // `showHero` permet de scinder le widget en DEUX blocs de page : un premier
  // avec le bandeau et le graphique, un second avec le seul répertoire des
  // devises, pour intercaler un bloc CMS entre les deux. Ces trois drapeaux
  // (`showHero`, `showChart`, `showAllCurrencies`) sont absents du fragment
  // cuit, donc une valeur posée sur la page survit à la fusion.
  const showHero = (content as any).showHero !== false;
  // Fallback flag map covering every currency surfaced by the BCC snapshot
  // feed (apps.bcc.cd/api/exchange-rate). Used when the page payload doesn't
  // carry an explicit `flags` map from the projector : keeps the "Toutes les
  // devises" table from rendering bare codes. XDR (IMF SDR) has no country
  // flag and is intentionally absent.
  const CURRENCY_FLAGS: Record<string, string> = (content as any).flags ?? {
    AED: "ae", AOA: "ao", AUD: "au", BIF: "bi", CAD: "ca", CDF: "cd",
    CHF: "ch", CNY: "cn", EUR: "eu", GBP: "gb", INR: "in", JPY: "jp",
    KWD: "kw", RWF: "rw", SAR: "sa", TZS: "tz", UGX: "ug", USD: "us",
    XAF: "cf", ZAR: "za", ZMW: "zm",
  };
  const defaultFrom = (content as any).defaultFrom ?? "CDF";
  const defaultTo = (content as any).defaultTo ?? "USD";
  const defaultActive = (content as any).defaultActive ?? "USD";
  const asOfDate = ((content as any).asOfDate ?? "").toString();
  // ----- CMS-driven text knobs ----- authors override these via the
  // FxRatesExplorer dynamicComponent config; defaults below keep the page
  // sensible when nothing is supplied. Pinning all user-facing copy here
  // means no future tweak needs a code edit. These knobs are widget chrome
  // that never passes the per-field translation pipeline, so a missing value
  // OR one still equal to the French default is swapped per locale; a
  // customized label renders verbatim in every language.
  const localizedKnob = (raw: unknown, frDefault: string, key: UiStringKey): string => {
    const value = (raw ?? "").toString().trim();
    return !value || value === frDefault ? tr(key) : value;
  };
  const heroEyebrow = localizedKnob((content as any).heroEyebrow, "Cours indicatif du jour", "fxHeroEyebrow");
  const converterTitle = localizedKnob((content as any).converterTitle, "Convertisseur", "fxConverterTitle");
  const converterCaption = localizedKnob((content as any).converterCaption, "Taux indicatifs BCC", "fxConverterCaption");
  const allCurrenciesTitle = ((content as any).allCurrenciesTitle ?? tr("allCurrencies")).toString();
  // Authors can still supply a disclaimer via the CMS; default is empty so
  // pages without an authored value render no disclaimer block.
  const disclaimerText = ((content as any).disclaimerText ?? "").toString();
  // Where the blue conversion-result tile sits on small screens. The CMS
  // controls this so authors can opt into a more prominent layout without
  // a code edit; defaults preserve the legacy behaviour for any older
  // payload that doesn't carry the field.
  //   "card"     : result tile stays inside the converter card (default).
  //   "detached" : result tile renders inside the card on desktop (≥ md)
  //                AND as a standalone block under the card on mobile,
  //                giving the conversion its own visual beat on phones.

  const [q, setQ] = useState("");
  // Held as a raw string (not a number) so the field can be fully cleared on
  // mobile : an empty input must stay empty instead of snapping back to 0.
  const [amount, setAmount] = useState<string>("1");
  const [from, setFrom] = useState<string>(defaultFrom);
  const [to, setTo] = useState<string>(defaultTo);
  const [activeCode, setActiveCode] = useState<string>(defaultActive);
  const [sortKey, setSortKey] = useState<"code" | "rate">("code");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  // 3 mois par défaut : la fenêtre qui montre la tendance récente sans écraser
  // le détail quotidien. 1 an aplatissait tout sur un seul décrochage.
  const [chartRange, setChartRange] = useState<FxChartRange>(plages.includes("3m") ? "3m" : plages[0]);
  const [chartMode, setChartMode] = useState<ChartMode>("level");
  // Aucune comparaison à l'ouverture : la page répond d'abord à la question
  // « combien vaut le dollar », superposer une seconde devise est un geste
  // volontaire du visiteur.
  const [compareCode, setCompareCode] = useState<string>("");

  const codeToRate = useMemo(() => {
    return RATES.reduce((acc, r) => { acc[r.code] = r; return acc; }, {} as Record<string, Rate>);
  }, [RATES]);

  // Currency display names: the feed publishes FRENCH names ("Dollar américain").
  // Other locales resolve the ISO code through Intl.DisplayNames (CLDR data),
  // falling back to the feed name when the runtime has no data for that
  // locale/code. French keeps the feed name byte-identical.
  const displayNameByCode = useMemo(() => {
    const out: Record<string, string> = {};
    let displayNames: Intl.DisplayNames | null = null;
    if (locale !== "fr") {
      try {
        displayNames = new Intl.DisplayNames([locale === "en" ? "en-GB" : locale], { type: "currency" });
      } catch {
        displayNames = null;
      }
    }
    for (const r of RATES) {
      let name = r.name;
      if (displayNames) {
        try {
          const resolved = displayNames.of(r.code);
          if (resolved && resolved !== r.code) {
            name = resolved.charAt(0).toUpperCase() + resolved.slice(1);
          }
        } catch { /* unknown code : keep the feed name */ }
      }
      out[r.code] = name;
    }
    return out;
  }, [RATES, locale]);
  const displayName = (r: Rate) => displayNameByCode[r.code] ?? r.name;

  // True when at least one currency has acheteur ≠ vendeur : the rare case
  // where the BCC publishes a real bid/ask spread. False (the common case)
  // collapses the column into a single "Cours (CDF)" header.
  const hasBuySellSplit = useMemo(
    () => RATES.some((r) => Math.abs(r.buy - r.sell) > 1e-9),
    [RATES],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const arr = RATES.filter((r) =>
      !term ||
      r.code.toLowerCase().includes(term) ||
      r.name.toLowerCase().includes(term) ||
      displayName(r).toLowerCase().includes(term));
    const dir = sortDir === "asc" ? 1 : -1;
    return [...arr].sort((a, b) => {
      if (sortKey === "code") return a.code.localeCompare(b.code) * dir;
      return (toMid(a) - toMid(b)) * dir;
    });
  }, [q, sortKey, sortDir, RATES, displayNameByCode]);

  function convert(value: number, fromCode: string, toCode: string): number {
    if (fromCode === toCode) return value;
    const fromRate = codeToRate[fromCode];
    const toRate = codeToRate[toCode];
    const fromMid = fromRate ? toUnitMid(fromRate) : 1;
    const toMidV = toRate ? toUnitMid(toRate) : 1;
    const inCDF = fromCode === "CDF" ? value : value * fromMid;
    return toCode === "CDF" ? inCDF : inCDF / toMidV;
  }
  // Coerce the raw string to a number only for the math. Empty / partial
  // input ("", "-", ".") parses to a non-finite value → treat as 0 without
  // ever writing 0 back into the field.
  const amountNum = useMemo(() => {
    const n = parseFloat(amount);
    return Number.isFinite(n) ? n : 0;
  }, [amount]);
  const conversion = useMemo(() => convert(amountNum, from, to), [amountNum, from, to]);

  // USD/CDF reference : the headline number on every BCC page. Same shape
  // as the home MarketsBoard's "1 USD = ..." card.
  const usdMid = useMemo(() => {
    const usd = codeToRate["USD"]; return usd ? toMid(usd) : 0;
  }, [codeToRate]);
  const usdChange = codeToRate["USD"]?.change1d ?? 0;

  // Le repli `series` est un simple tableau de nombres, sans date : il ne peut
  // se decouper qu'au compte. Ordres de grandeur en jours ouvres.
  const rangeSize = RANGE_APPROX_POINTS[chartRange];
  const activeHistory = useMemo(() => windowByDate(HISTORY[activeCode] ?? [], chartRange), [HISTORY, activeCode, chartRange]);
  const seriesForActive = useMemo(() => {
    if (activeHistory.length > 0) {
      return activeHistory.map(point => point.average / Math.max(1, point.unit ?? 1));
    }
    return (SERIES[activeCode] || []).slice(-rangeSize);
  }, [SERIES, activeCode, activeHistory, rangeSize]);
  const datesForActive = useMemo(() => activeHistory.map(point => point.date), [activeHistory]);
  const comparisonSeries = useMemo(() => {
    const history = windowByDate(HISTORY[compareCode] ?? [], chartRange);
    if (history.length > 0) {
      return history.map(point => point.average / Math.max(1, point.unit ?? 1));
    }
    return (SERIES[compareCode] || []).slice(-rangeSize);
  }, [HISTORY, SERIES, compareCode, chartRange, rangeSize]);
  const activeRate = codeToRate[activeCode];
  const rangeChange = percentChange(seriesForActive);
  const volatility = annualizedVolatility(seriesForActive);
  const spread = activeRate ? Math.max(0, activeRate.sell - activeRate.buy) : 0;
  const spreadPct = activeRate && toMid(activeRate) !== 0 ? spread / toMid(activeRate) : 0;

  const exportActiveHistory = () => {
    const history = HISTORY[activeCode] ?? [];
    const lines = [tr("fxCsvHeader")];
    history.forEach(point => lines.push([
      point.date,
      activeCode,
      point.unit ?? 1,
      point.buy,
      point.average,
      point.sell,
    ].join(",")));
    if (history.length === 0) {
      (SERIES[activeCode] ?? []).forEach((value, index) => lines.push([index + 1, activeCode, 1, value, value, value].join(",")));
    }
    const url = URL.createObjectURL(new Blob([`\uFEFF${lines.join("\n")}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `bcc-cours-${activeCode.toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Picking a currency in the table or a hero chip should do three things
   * at once: focus its chart, *and* pre-fill the converter as "from CDF to
   * <code>" so the visible blue result instantly reflects the user's pick.
   * CDF is the canonical source because Congolese visitors most often ask
   * "how much is N CDF worth in X?" : picking USD does NOT replace CDF on
   * the FROM side, which would loop the converter on itself. Clicking CDF
   * itself (it's not in the table today but might be in future) swaps
   * direction to "from USD to CDF".
   */
  const selectCurrency = (code: string) => {
    // Clicking the already-active currency "unclicks" it and restores the
    // default converter state (default active currency + default from/to),
    // instead of leaving the last selection stuck.
    if (code === activeCode) {
      setActiveCode(defaultActive);
      setFrom(defaultFrom);
      setTo(defaultTo);
      return;
    }
    if (code === compareCode) {
      setCompareCode(RATES.find(rate => rate.code !== code && rate.code !== "CDF")?.code ?? "");
    }
    setActiveCode(code);
    if (code === "CDF") {
      setFrom("USD");
      setTo("CDF");
    } else {
      setFrom("CDF");
      setTo(code);
    }
  };

  // The hero secondary chips : three currencies after USD with their own
  // mini sparkline. Picked by data order so authors who change `currencies`
  // in the dynamic config get matching chips without further config.
  // Vignettes de référence : USD, EUR, GBP dans CET ordre, le même que le
  // bandeau d'indicateurs du site. Elles ne dépendent PAS de la devise active
  // ; les retirer au fur et à mesure de la sélection empêchait justement de
  // revenir sur le dollar d'un clic.
  const secondaryCodes = useMemo(() => {
    const preferred = ["USD", "EUR", "GBP", "CNY", "ZAR", "CHF"];
    const byCode = new Map(RATES.map((rate) => [rate.code, rate]));
    const picked = preferred.map((code) => byCode.get(code)).filter((rate): rate is Rate => Boolean(rate));
    const rest = RATES.filter((rate) => rate.code !== "CDF" && !preferred.includes(rate.code));
    return [...picked, ...rest].slice(0, 3);
  }, [RATES]);

  return (
    <>
      {/* ---------------------------------------------------------------- */}
      {!showHero ? null : (<>
      {/* HERO : panneau BLANC, comme tous les autres panneaux de la page. */}
      {/* Même bordure et même ombre que les cartes du site, pour que la    */}
      {/* page ne présente qu'un seul type de surface.                      */}
      {/* ---------------------------------------------------------------- */}
      <section className="mb-8 overflow-hidden rounded-md border border-bcc-panel bg-white text-[#0F2849] shadow-bcc-panel">
        <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[1.5fr_2fr] lg:gap-10">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#005CA9]">
              <span>{heroEyebrow}</span>
              {asOfDate ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-[#005CA9]/20 bg-[#EAF6FF] px-2.5 py-0.5 text-[#0F2849]">
                  <Calendar className="h-3 w-3" aria-hidden="true" /> {asOfDate}
                </span>
              ) : null}
            </div>
            <div className="mt-4">
              <div className="text-sm font-medium text-[#0F2849]/70">{tr("fxHeroUnitLabel")}</div>
              <div className="mt-1 flex flex-wrap items-baseline gap-3 leading-none">
                <span className="text-4xl font-bold tabular-nums text-[#005CA9] sm:text-5xl">{formatAmount(usdMid, locale)}</span>
                <span className="text-lg font-semibold text-[#0F2849]/70">CDF</span>
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-[#005CA9]">{tr("otherCurrencies")}</div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {secondaryCodes.map((r) => {
                const up = r.change1d >= 0;
                const Trend = up ? TrendingUp : TrendingDown;
                const series = (SERIES[r.code] || []).slice(-60);
                const selected = activeCode === r.code;
                return (
                  <button
                    type="button"
                    key={r.code}
                    onClick={() => selectCurrency(r.code)}
                    aria-pressed={selected}
                    className={`group cursor-pointer rounded-lg border px-3 py-3 text-left transition focus:outline-none focus:ring-2 focus:ring-[#005CA9]/40 ${selected ? "border-[#005CA9] bg-[#EAF6FF]" : "border-[#005CA9]/15 bg-[#F4F8FC] hover:bg-[#EAF6FF]"}`}
                  >
                    <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-[#005CA9]">
                      <span>{r.code}/CDF</span>
                      <Trend className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <div className="mt-1 text-lg font-semibold tabular-nums text-[#0F2849]">{formatAmount(toMid(r), locale)}</div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className={`text-xs font-medium ${up ? "text-emerald-600" : "text-rose-600"}`}>
                        {up ? "+" : ""}{(r.change1d * 100).toFixed(2)} %
                      </span>
                      {series.length > 1 ? (
                        <MiniSparkline data={series} color={up ? "#059669" : "#e11d48"} width={64} height={20} />
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      </>)}

      {showAnalytics ? (
      <section className="mb-8 rounded-md bg-white p-5 border border-bcc-panel shadow-bcc-panel sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[#005CA9]">
              <Activity className="h-4 w-4" aria-hidden="true" />
              <p className="text-xs font-semibold uppercase tracking-[0.16em]">{tr("fxAnalyticsTitle")}</p>
            </div>
            <p className="mt-2 text-sm text-slate-500">{tr("fxMethodology")}</p>
          </div>
          <button
            type="button"
            onClick={exportActiveHistory}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-[#005CA9]/30 bg-white px-3 text-xs font-semibold text-[#005CA9] transition hover:bg-[#EAF6FF]"
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" /> {tr("fxExportCsv")}
          </button>
        </div>
        <div className="mt-5 grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-2 xl:grid-cols-4">
          {[
            {
              label: tr("fxSelectedRate"),
              value: `${formatAmount(activeRate ? toUnitMid(activeRate) : 0, locale)} CDF`,
              note: `1 ${activeCode}`,
              tone: "text-[#005CA9]",
            },
            {
              label: tr("fxRangeChange"),
              value: `${rangeChange >= 0 ? "+" : ""}${(rangeChange * 100).toFixed(2)} %`,
              note: chartRange,
              tone: rangeChange >= 0 ? "text-emerald-700" : "text-rose-700",
            },
            {
              label: tr("fxVolatility"),
              value: `${(volatility * 100).toFixed(2)} %`,
              note: `${seriesForActive.length} ${tr("fxObsAbbrev")}`,
              tone: "text-[#0F2849]",
            },
            {
              label: tr("fxSpread"),
              value: `${formatNumber(spread / Math.max(1, activeRate?.unit ?? 1), locale, { maximumFractionDigits: 4 })} CDF`,
              note: `${(spreadPct * 100).toFixed(3)} %`,
              tone: "text-[#0F2849]",
            },
          ].map(metric => (
            <div key={metric.label} className="bg-white p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{metric.label}</p>
              <p className={`mt-2 text-xl font-bold tabular-nums ${metric.tone}`}>{metric.value}</p>
              <p className="mt-1 text-xs text-slate-400">{metric.note}</p>
            </div>
          ))}
        </div>
      </section>
      ) : null}

      {/* ---------------------------------------------------------------- */}
      {/* PRIMARY ACTION : converter + chart, side by side on desktop;     */}
      {/* on mobile the chart sits on TOP (order-1) so users see the trend */}
      {/* for the active currency before the inputs, and the converter     */}
      {/* form follows directly underneath (order-2).                       */}
      {/* ---------------------------------------------------------------- */}
      {variant === "market" ? (
        showChart ? (
        <section className="mb-8">
          <ChartCard
            activeCode={activeCode}
            series={seriesForActive}
            dates={datesForActive}
            rates={RATES}
            compareCode={compareCode}
            comparisonSeries={comparisonSeries}
            chartMode={chartMode}
            ranges={plages}
            chartRange={chartRange}
            onRangeChange={setChartRange}
            onCompareCode={setCompareCode}
            onChartMode={setChartMode}
            changePct={codeToRate[activeCode]?.change1d ?? 0}
            flags={CURRENCY_FLAGS}
            nameOf={(code) => displayNameByCode[code] ?? code}
          />
        </section>
        ) : null
      ) : (
        <section className={`mb-8 grid items-stretch gap-4 ${showChart ? "lg:grid-cols-2" : ""}`}>
          <div className="order-2 flex lg:order-1">
            <ConverterCard
              title={converterTitle}
              caption={converterCaption}
              rates={RATES}
              amount={amount}
              amountNum={amountNum}
              from={from}
              to={to}
              conversion={conversion}
              onAmount={setAmount}
              onFrom={setFrom}
              onTo={setTo}
              onSwap={() => { const f = from; setFrom(to); setTo(f); }}
            />
          </div>
          {showChart ? (
          <div className="order-1 flex lg:order-2">
            <ChartCard
              activeCode={activeCode}
              series={seriesForActive}
              dates={datesForActive}
              rates={RATES}
              compareCode={compareCode}
              comparisonSeries={comparisonSeries}
              chartMode={chartMode}
              ranges={plages}
              chartRange={chartRange}
              onRangeChange={setChartRange}
              onCompareCode={setCompareCode}
              onChartMode={setChartMode}
              changePct={codeToRate[activeCode]?.change1d ?? 0}
              flags={CURRENCY_FLAGS}
              nameOf={(code) => displayNameByCode[code] ?? code}
            />
          </div>
          ) : null}
        </section>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* ALL CURRENCIES : search + table (desktop) / cards (mobile) with  */}
      {/* a mini sparkline per row so users see the trend at a glance.     */}
      {/* Masquable depuis le CMS : une page outil (le convertisseur) n'a  */}
      {/* pas à répéter le répertoire complet de la page des cours.        */}
      {/* Table masquée => rien du tout : les renvois croisés vivent dans  */}
      {/* le bloc « Voir aussi », qui est éditable depuis l'admin. Un seul */}
      {/* endroit porte les liens entre pages, pas deux.                   */}
      {/* ---------------------------------------------------------------- */}
      {/* Sans bandeau, ce répertoire est un bloc de page à part entière, posé
          après un autre bloc : il porte alors sa propre marge haute, dont le
          widget complet n'a pas besoin. */}
      {!showAllCurrencies ? null : (
      <section className={showHero ? "" : "mt-10"}>
        {/* Pas de bouton « ouvrir le convertisseur » ici : le bloc « Voir
            aussi » en pied de page porte déjà ce renvoi, et un seul endroit
            doit tenir les liens entre pages. */}
        <div className="mb-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#E98A00]">{tr("fxTableEyebrow")}</p>
          <h2 className="mt-1 text-xl font-bold text-[#005CA9]" style={{ fontFamily: "var(--font-aleo)" }}>{allCurrenciesTitle}</h2>
        </div>
        <div className="mb-3 relative">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-10 w-full rounded-md border border-bcc-panel bg-white pl-10 pr-3 text-sm text-[#0F2849] placeholder:text-slate-400 focus:border-[#005CA9] focus:outline-none focus:ring-1 focus:ring-[#005CA9]"
            placeholder={tr("searchCurrencyPlaceholder")}
            aria-label={tr("searchCurrency")}
          />
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        </div>

        {/* Desktop / tablet table : adds an inline sparkline column. */}
        <div className="hidden overflow-hidden rounded-md border border-slate-200 bg-white lg:block">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                  onClick={() => { setSortKey("code"); setSortDir((d) => d === "asc" ? "desc" : "asc"); }}
                >
                  {tr("fxCodeHeader")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">{tr("currencyHeader")}</th>
                {hasBuySellSplit ? (
                  <>
                    <th
                      className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                      onClick={() => { setSortKey("rate"); setSortDir((d) => d === "asc" ? "desc" : "asc"); }}
                    >
                      {tr("midHeader")}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">{tr("buyHeader")}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">{tr("sellHeader")}</th>
                  </>
                ) : (
                  <th
                    className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer"
                    onClick={() => { setSortKey("rate"); setSortDir((d) => d === "asc" ? "desc" : "asc"); }}
                  >
                    {tr("rateCdfHeader")}
                  </th>
                )}
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">{tr("oneDayHeader")}</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">{tr("fxSevenDayChange")}</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">{tr("trend60Header")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {filtered.map((r) => {
                const up1 = r.change1d >= 0;
                const Icon1 = up1 ? ArrowUpRight : ArrowDownRight;
                const up7 = r.change7d >= 0;
                const Icon7 = up7 ? ArrowUpRight : ArrowDownRight;
                const flag = CURRENCY_FLAGS[r.code];
                const selected = activeCode === r.code;
                const series = (SERIES[r.code] || []).slice(-60);
                return (
                  <tr
                    key={r.code}
                    className={`cursor-pointer transition ${selected ? "bg-[#EAF6FF]" : "hover:bg-slate-50"}`}
                    onClick={() => selectCurrency(r.code)}
                    aria-selected={selected}
                  >
                    <td className="px-4 py-3 text-sm font-semibold text-[#005CA9]">{r.code}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      <span className="inline-flex items-center gap-2">{flag && <span className={`fi fi-${flag}`}></span>} {displayName(r)}</span>
                    </td>
                    {hasBuySellSplit ? (
                      <>
                        <td className="px-4 py-3 text-right font-mono text-sm font-semibold tabular-nums text-[#0F2849]">{formatAmount(toMid(r), locale)}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-[#0F2849]">{formatAmount(r.buy, locale)}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-[#0F2849]">{formatAmount(r.sell, locale)}</td>
                      </>
                    ) : (
                      <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-[#0F2849]">{formatAmount(toMid(r), locale)}</td>
                    )}
                    <td className={`px-4 py-3 text-right text-sm whitespace-nowrap ${up1 ? "text-emerald-600" : "text-rose-600"}`}>
                      <span className="inline-flex items-center gap-1"><Icon1 className="h-3.5 w-3.5" />{Math.abs(r.change1d * 100).toFixed(2)} %</span>
                    </td>
                    <td className={`px-4 py-3 text-right text-sm whitespace-nowrap ${up7 ? "text-emerald-600" : "text-rose-600"}`}>
                      <span className="inline-flex items-center gap-1"><Icon7 className="h-3.5 w-3.5" />{Math.abs(r.change7d * 100).toFixed(2)} %</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {series.length > 1 ? (
                        <span className="inline-block align-middle">
                          <MiniSparkline data={series} color={up1 ? "#059669" : "#e11d48"} width={92} height={26} />
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={hasBuySellSplit ? 8 : 6} className="px-4 py-8 text-center text-sm text-slate-500">{tr("noResults")}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked cards (table is unreadable narrow). */}
        <div className="grid grid-cols-1 gap-3 lg:hidden">
          {filtered.map((r) => {
            const up1 = r.change1d >= 0;
            const Icon1 = up1 ? ArrowUpRight : ArrowDownRight;
            const up7 = r.change7d >= 0;
            const Icon7 = up7 ? ArrowUpRight : ArrowDownRight;
            const flag = CURRENCY_FLAGS[r.code];
            const selected = activeCode === r.code;
            const series = (SERIES[r.code] || []).slice(-60);
            return (
              <button
                key={r.code}
                onClick={() => selectCurrency(r.code)}
                className={`w-full cursor-pointer rounded-lg border bg-white p-4 text-left transition ${selected ? "border-[#005CA9] bg-[#EAF6FF]" : "border-slate-200 hover:border-[#005CA9]/40"}`}
                aria-pressed={selected}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {flag && <span className={`fi fi-${flag}`}></span>}
                    <div className="font-semibold text-[#005CA9]">{r.code}</div>
                  </div>
                  <div className="text-xs text-slate-500">{displayName(r)}</div>
                </div>
                <div className="mt-3 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">{tr("rateCdfHeader")}</div>
                    <div className="mt-1 font-mono text-lg font-semibold tabular-nums text-[#0F2849]">{formatAmount(toMid(r), locale)}</div>
                  </div>
                  {series.length > 1 ? (
                    <MiniSparkline data={series} color={up1 ? "#059669" : "#e11d48"} width={80} height={28} />
                  ) : null}
                </div>
                <div className="mt-2 flex flex-wrap gap-3">
                  <span className={`inline-flex items-center gap-1 text-xs ${up1 ? "text-emerald-600" : "text-rose-600"}`}>
                    <Icon1 className="h-4 w-4" />{Math.abs(r.change1d * 100).toFixed(2)} {tr("percentOneDay")}
                  </span>
                  <span className={`inline-flex items-center gap-1 text-xs ${up7 ? "text-emerald-600" : "text-rose-600"}`}>
                    <Icon7 className="h-4 w-4" />{Math.abs(r.change7d * 100).toFixed(2)} % · {tr("fxSevenDayChange")}
                  </span>
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">{tr("noResults")}</div>
          )}
        </div>
      </section>
      )}

      {disclaimerText ? <p className="mt-6 text-xs text-slate-500">{disclaimerText}</p> : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// ConverterCard : promoted from the right-side aside to a first-class panel.
// Title + caption come from CMS so authors can rename without code edits.
// The previous accent-orange left rail was removed at the user's request.
// Against the otherwise all-blue chrome it read as a stray yellow stripe.
// The brand-blue result tile at the bottom stays as the visual anchor.
// ---------------------------------------------------------------------------
function ConverterCard(props: {
  title: string;
  caption: string;
  rates: Rate[];
  amount: string;
  amountNum: number;
  from: string;
  to: string;
  conversion: number;
  onAmount: (s: string) => void;
  onFrom: (s: string) => void;
  onTo: (s: string) => void;
  onSwap: () => void;
}) {
  const { title, caption, rates, amount, amountNum, from, to, conversion, onAmount, onFrom, onTo, onSwap } = props;
  const locale = useLocale();
  const tr = useT();
  return (
    <section className="flex w-full flex-col rounded-md bg-white p-5 border border-bcc-panel shadow-bcc-panel sm:p-6">
      {/* Même en-tête que le panneau graphique : sur-titre ambre puis titre
          bleu en Aleo. Le gris de départ était la dernière exception. */}
      <header className="border-b border-[#DCE6ED] pb-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#E98A00]">{caption}</p>
        <h2 className="mt-1 text-xl font-bold text-[#005CA9]" style={{ fontFamily: "var(--font-aleo)" }}>{title}</h2>
      </header>
      <div className="mt-4 flex flex-1 flex-col gap-3">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr("amount")}</span>
          <input
            type="number"
            inputMode="decimal"
            placeholder={tr("fxAmountPlaceholder")}
            className="mt-1 h-11 w-full rounded-md border border-bcc-panel bg-white px-3 text-lg font-semibold text-[#0F2849] placeholder:font-normal placeholder:text-slate-400 outline-none focus:border-[#005CA9] focus:ring-1 focus:ring-[#005CA9]"
            value={amount}
            min={0}
            onChange={(e) => onAmount(e.target.value)}
          />
        </label>
        <div className="flex flex-nowrap items-end gap-2 w-full">
          <label className="block min-w-0 flex-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr("fromCurrency")}</span>
            <select
              name="from"
              aria-label={tr("sourceCurrency")}
              value={from}
              onChange={(e) => onFrom(e.target.value)}
              className="mt-1 h-11 w-full rounded-md border border-bcc-panel bg-white px-3 text-[#0F2849] outline-none focus:border-[#005CA9] focus:ring-1 focus:ring-[#005CA9]"
            >
              {/* CDF is the local currency and always a valid converter
                  endpoint, but the projector emits foreign rates only
                  (since you can't quote CDF in CDF). Inject it as a
                  synthetic first option so users can pick it on either
                  side of the converter. */}
              {!rates.some((r) => r.code === "CDF") ? (
                <option value="CDF">CDF</option>
              ) : null}
              {rates.map((r) => (<option key={r.code} value={r.code}>{r.code}</option>))}
            </select>
          </label>
          <button
            type="button"
            className="inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md border border-bcc-panel bg-white text-slate-600 transition hover:border-[#005CA9] hover:text-[#005CA9]"
            onClick={onSwap}
            aria-label={tr("swapCurrencies")}
          >
            <ArrowLeftRight className="h-4 w-4" />
          </button>
          <label className="block min-w-0 flex-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr("toCurrency")}</span>
            <select
              name="to"
              aria-label={tr("targetCurrency")}
              value={to}
              onChange={(e) => onTo(e.target.value)}
              className="mt-1 h-11 w-full rounded-md border border-bcc-panel bg-white px-3 text-[#0F2849] outline-none focus:border-[#005CA9] focus:ring-1 focus:ring-[#005CA9]"
            >
              {/* CDF is the local currency and always a valid converter
                  endpoint, but the projector emits foreign rates only
                  (since you can't quote CDF in CDF). Inject it as a
                  synthetic first option so users can pick it on either
                  side of the converter. */}
              {!rates.some((r) => r.code === "CDF") ? (
                <option value="CDF">CDF</option>
              ) : null}
              {rates.map((r) => (<option key={r.code} value={r.code}>{r.code}</option>))}
            </select>
          </label>
        </div>
        {/* Pavé bleu du résultat, collé EN BAS de la carte : le panneau est
            étiré à la hauteur du graphique voisin, sans `mt-auto` le blanc
            restant tombait sous le résultat au lieu d'être absorbé. */}
        <div className="mt-auto">
          <ResultPanel amount={amountNum} from={from} to={to} conversion={conversion} />
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ResultPanel : the brand-blue conversion-result tile. Extracted from the
// converter card so it can also be rendered as a detached mobile block
// without duplicating the markup.
// ---------------------------------------------------------------------------
function ResultPanel({ amount, from, to, conversion }: { amount: number; from: string; to: string; conversion: number }) {
  const locale = useLocale();
  const tr = useT();
  return (
    <div
      className="grid place-items-center rounded-md px-4 py-4 text-white"
      style={{ background: COLOR_PRIMARY }}
    >
      <div className="text-center">
        <div className="text-xs font-semibold uppercase tracking-wide text-white/80">
          {formatNumber(amount, locale)} {from} {tr("equalsTo")}
        </div>
        <div className="mt-1">
          <span className="font-mono text-2xl font-bold tabular-nums">{formatConversion(conversion, locale)}</span>{" "}
          <span className="text-base text-white/80">{to}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChartCard : focused chart for the actively-selected currency, range
// toggles top-right, 1-day delta badge so the user sees the headline number
// without reading axes.
// ---------------------------------------------------------------------------
/**
 * Sélecteur de devise : bouton + liste, à la place du `<select>` natif.
 *
 * Le `<select>` d'origine ne pouvait afficher qu'un code nu : un `<option>`
 * n'accepte pas d'élément enfant, donc pas de drapeau. Ce composant rend le
 * drapeau `flag-icons` (le même sprite que le tableau des cours), le code et
 * le nom de la devise, dans le bouton comme dans la liste. Il expose
 * `role="listbox"` et se ferme au clic extérieur ou sur Échap.
 */
function CurrencyPicker({
  value,
  options,
  flags,
  nameOf,
  noneLabel,
  onChange,
}: {
  value: string;
  options: string[];
  flags: Record<string, string>;
  nameOf: (code: string) => string;
  noneLabel: string;
  onChange: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const row = (code: string) => (
    <>
      {flags[code] ? (
        <span className={`fi fi-${flags[code]} shrink-0`} aria-hidden="true" />
      ) : (
        <span className="h-3 w-4 shrink-0 rounded-[2px] bg-slate-200" aria-hidden="true" />
      )}
      <span className="font-semibold tabular-nums">{code}</span>
      <span className="min-w-0 truncate text-slate-500">{nameOf(code)}</span>
    </>
  );

  return (
    <div ref={rootRef} className="relative mt-1">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-9 w-56 cursor-pointer items-center gap-2 rounded-md border border-bcc-panel bg-white px-3 text-sm text-[#0F2849] transition hover:border-[#005CA9]/40 focus:border-[#005CA9] focus:outline-none focus:ring-1 focus:ring-[#005CA9]"
      >
        {value ? row(value) : <span className="text-slate-500">{noneLabel}</span>}
        <ChevronDown className={`ml-auto h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open ? (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-72 overflow-auto rounded-md border border-bcc-panel bg-white py-1 shadow-bcc-panel"
        >
          <li>
            <button
              type="button"
              role="option"
              aria-selected={!value}
              onClick={() => { onChange(""); setOpen(false); }}
              className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-[#EAF6FF] ${!value ? "bg-[#EAF6FF]" : ""}`}
            >
              <span className="text-slate-500">{noneLabel}</span>
              {!value ? <Check className="ml-auto h-4 w-4 shrink-0 text-[#005CA9]" aria-hidden="true" /> : null}
            </button>
          </li>
          {options.map((code) => (
            <li key={code}>
              <button
                type="button"
                role="option"
                aria-selected={code === value}
                onClick={() => { onChange(code); setOpen(false); }}
                className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-[#EAF6FF] ${code === value ? "bg-[#EAF6FF]" : ""}`}
              >
                {row(code)}
                {code === value ? <Check className="ml-auto h-4 w-4 shrink-0 text-[#005CA9]" aria-hidden="true" /> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ChartCard(props: {
  activeCode: string;
  series: number[];
  dates: string[];
  rates: Rate[];
  compareCode: string;
  comparisonSeries: number[];
  chartMode: ChartMode;
  ranges: readonly FxChartRange[];
  chartRange: FxChartRange;
  onRangeChange: (r: FxChartRange) => void;
  onCompareCode: (code: string) => void;
  onChartMode: (mode: ChartMode) => void;
  changePct: number;
  flags: Record<string, string>;
  nameOf: (code: string) => string;
}) {
  const {
    activeCode,
    series,
    dates,
    rates,
    compareCode,
    comparisonSeries,
    chartMode,
    ranges,
    chartRange,
    onRangeChange,
    onCompareCode,
    onChartMode,
    changePct,
    flags,
    nameOf,
  } = props;
  const locale = useLocale();
  const tr = useT();
  const up = changePct >= 0;
  const Trend = up ? TrendingUp : TrendingDown;
  const comparing = Boolean(compareCode);
  return (
    <section className="flex w-full flex-col rounded-md bg-white p-5 border border-bcc-panel shadow-bcc-panel sm:p-6">
      {/* En-tête au gabarit des autres panneaux du site : sur-titre ambre en
          capitales, puis titre bleu en Aleo. Le `text-[#0F2849]` d'avant était
          la seule exception de la page. */}
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-[#DCE6ED] pb-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#E98A00]">{tr("fxChartEyebrow")}</p>
          <h2 className="mt-1 text-xl font-bold text-[#005CA9]" style={{ fontFamily: "var(--font-aleo)" }}>
            {tr("trendOf")} {activeCode}/CDF
          </h2>
          <div className={`mt-1.5 inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>
            <Trend className="h-3.5 w-3.5" aria-hidden="true" />
            {up ? "+" : ""}{(changePct * 100).toFixed(2)} {tr("percentOneDay")}
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {ranges.map((r) => (
            <button
              key={r}
              onClick={() => onRangeChange(r)}
              className={`cursor-pointer rounded-full border px-3 py-1 text-xs font-medium transition ${
                chartRange === r
                  ? "border-[#005CA9] bg-[#005CA9] text-white"
                  : "border-bcc-panel text-slate-700 hover:border-[#005CA9]/40"
              }`}
            >
              {tr(`fxPeriod${r}` as UiStringKey)}
            </button>
          ))}
        </div>
      </header>
      {/* Barre d'outils sans encadré : l'ancien bloc gris posé DANS un panneau
          blanc faisait une carte dans la carte. Les commandes se lisent mieux
          alignées sous l'en-tête. */}
      <div className="mt-4 flex flex-wrap items-end gap-4">
        <div>
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{tr("fxCompareWith")}</span>
          <CurrencyPicker
            value={compareCode}
            options={rates.filter((rate) => rate.code !== activeCode).map((rate) => rate.code)}
            flags={flags}
            nameOf={nameOf}
            noneLabel={tr("fxCompareNone")}
            onChange={onCompareCode}
          />
        </div>
        {/* Le bascule niveau / base 100 n'a de sens QUE face à une seconde
            devise : sur une courbe unique, les deux vues sont identiques à un
            facteur près. On ne l'affiche donc que pendant une comparaison. */}
        {comparing ? (
          <div className="flex gap-1 rounded-md border border-bcc-panel bg-white p-1">
            {(["level", "index"] as const).map(mode => (
              <button
                key={mode}
                type="button"
                onClick={() => onChartMode(mode)}
                className={`cursor-pointer rounded px-3 py-1.5 text-xs font-semibold transition ${chartMode === mode ? "bg-[#005CA9] text-white" : "text-slate-600 hover:bg-slate-100"}`}
              >
                {mode === "level" ? tr("fxLevelView") : tr("fxIndexView")}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="mt-4">
        <div className="mb-2 flex flex-wrap gap-4 text-xs font-semibold text-slate-600">
          <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-5 bg-[#005CA9]" />{activeCode}/CDF</span>
          {compareCode ? <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-5 border-t-2 border-dashed border-amber-500" />{compareCode}/CDF</span> : null}
        </div>
        <MainChart data={series} comparison={comparisonSeries} dates={dates} mode={chartMode} />
      </div>
    </section>
  );
}
